import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatPrice,
  calculateDaysDiff,
} from '../../utilities/FormatFunctions';
import {
  AreaIcon,
  BedroomsIcon,
  BathroomsIcon,
  GarageIcon,
  PriceIcon,
  CityIcon,
  LocationIcon,
} from '../../utilities/icons/RealEstatesIcons';
import { motion } from 'framer-motion';

export default function RealEstatesCard({
  id,
  coverImageUrl,
  name,
  businessType,
  isLot,
  area,
  city,
  sector,
  price,
  bedrooms,
  bathrooms,
  garage,
  createdAt,
}) {
  const navigate = useNavigate();
  const differenceInDays = calculateDaysDiff(createdAt);

  const handleClick = () => {
    navigate(`/ventas/detalle/${id}`); // Navigate to the detail page with the id
  };

  return (
    <div
      className="relative rounded-2xl shadow-lg overflow-hidden w-full aspect-[4/3] sm:aspect-[16/9] bg-background-light dark:bg-background-dark cursor-pointer group"
      onClick={handleClick}
    >
      {/* Imagen */}
      <img
        src={coverImageUrl}
        alt={name}
        className="w-full h-full object-cover"
      />

      {/* Gradiente principal */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />

      {/* Encabezado */}
      <div className="absolute inset-x-0 top-0 p-6">
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/30 to-transparent" />
        <div className="relative flex justify-between items-start text-white">
          <div>
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-xl xl:text-2xl font-heading font-bold">
              {name}
            </h2>
            <p className="text-md sm:text-lg md:text-xl lg:text-lg xl:text-xl font-semibold mt-1 inline-flex items-baseline whitespace-nowrap">
              <PriceIcon className="font-bold self-center" />
              {formatPrice(price)}
              {businessType === 'Arriendo' ? (
                <span className="text-[10px] sm:text-xs md:text-sm lg:text-xs xl:text-sm font-medium leading-none self-baseline relative -top-px">
                  /mes
                </span>
              ) : null}
            </p>
          </div>
        </div>
      </div>

      {/* Información inferior */}
      <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
        <div
          className={`grid ${
            isLot
              ? 'grid-cols-3 gap-x-10 md:gap-x-8 lg:gap-x-10 xl:gap-x-8'
              : 'grid-cols-4 gap-x-8 md:gap-x-6 lg:gap-x-8 xl:gap-x-6'
          } gap-y-4 text-white`}
        >
          {isLot ? (
            <>
              <div className="flex items-center">
                <AreaIcon className="text-white mr-2 md:mr-3 lg:mr-2 xl:mr-3 text-sm md:text-xl lg:text-sm xl:text-xl" />
                <div>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-medium">
                    Área
                  </p>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-bold">
                    {area} m²
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <CityIcon className="text-white mr-2 md:mr-3 lg:mr-2 xl:mr-3 text-sm md:text-xl lg:text-sm xl:text-xl" />
                <div>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-medium">
                    Ciudad
                  </p>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-bold">
                    {city}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <LocationIcon className="text-white mr-2 md:mr-3 lg:mr-2 xl:mr-3 text-sm md:text-xl lg:text-sm xl:text-xl" />
                <div>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-medium">
                    Sector
                  </p>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-bold">
                    {sector}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center">
                <AreaIcon className="text-white mr-2 md:mr-3 lg:mr-2 xl:mr-3 text-sm md:text-xl lg:text-sm xl:text-xl" />
                <div>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-medium">
                    Área
                  </p>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-bold">
                    {area} m²
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <BedroomsIcon className="text-white mr-2 md:mr-3 lg:mr-2 xl:mr-3 text-sm md:text-xl lg:text-sm xl:text-xl" />
                <div>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-medium">
                    Cuartos
                  </p>
                  <p className="text-xs md:text-smlg:text-xs xl:text-sm font-bold">
                    {bedrooms}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <BathroomsIcon className="text-white mr-2 md:mr-3 lg:mr-2 xl:mr-3 text-sm md:text-xl lg:text-sm xl:text-xl" />
                <div>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-medium">
                    Baños
                  </p>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-bold">
                    {bathrooms}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <GarageIcon className="text-white mr-2 md:mr-3 lg:mr-2 xl:mr-3 text-sm md:text-xl lg:text-sm xl:text-xl" />
                <div>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-medium">
                    Garaje
                  </p>
                  <p className="text-xs md:text-sm lg:text-xs xl:text-sm font-bold">
                    {garage ? 'Sí' : 'No'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Etiqueta horizontal amarilla, animación solo en el texto */}
      {differenceInDays <= 15 && (
        <span
          className="absolute top-0 right-0 bg-amber-300 font-heading tracking-wide text-gray-800 font-bold text-[10px] sm:text-xs px-3 sm:px-4 py-1 sm:py-2 shadow-lg rounded-bl-xl rounded-tr-xl z-30"
          style={{ letterSpacing: '0.05em' }}
        >
          <motion.span
            initial={{ scale: 1, opacity: 0.8 }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{ display: 'inline-block' }}
          >
            ¡Nuevo!
          </motion.span>
        </span>
      )}
    </div>
  );
}
